<template>
  <div>
    <v-dialog v-model="editModal" max-width="500px">
      <v-toolbar dark color="primary">
        <v-toolbar-title>{{ editedItem.TrackingNumber }}</v-toolbar-title>
        <v-btn
          class="close-edit-order-btn"
          icon
          dark
          style="position: absolute; right: 20px; top: 8px;"
          @click.native="close"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card class="edit-move-modal-v-card">
        <v-card-title>
          <h3>{{ editedItem.ClientName }}: {{ editedItem.LocationName }}</h3>
        </v-card-title>
        <v-card-text>
          <v-expansion-panels v-model="openedPanel">
            <v-expansion-panel expand @change="loadNotes()">
              <v-expansion-panel-header>
                <div class="notes-header">Notes</div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <center>
                  <v-progress-circular
                    v-if="notesLoading"
                    indeterminate
                    color="blue"
                  />
                </center>
                <v-list three-line>
                  <template v-for="item in editedItem.notes">
                    <div :key="item.id" class="note-item">
                      <v-divider></v-divider>
                      <v-list-item :key="item.id" avatar>
                        <v-list-item-avatar :color="getUserColor(item)">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <span v-on="on">
                                {{ getUserAbbreviation(item.CreatedBy) }}
                              </span>
                            </template>
                            <span>{{ getUserTip(item) }}</span>
                          </v-tooltip>
                        </v-list-item-avatar>
                        <v-list-item-content
                          @click="showNoteDetail(item.Content)"
                        >
                          <v-list-item-title>
                            {{ item.DateCreated | moment("M/D/YY, h:mm A") }}
                            {{ item.CreatedBy }}
                          </v-list-item-title>
                          <v-list-item-subtitle
                            v-html="item.Content"
                          ></v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </div>
                  </template>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel expand @change="loadNotes()">
              <v-expansion-panel-header>
                <div class="activities-header">Activities</div>
              </v-expansion-panel-header>
              <v-expansion-panel-content @input="loadNotes()">
                <center>
                  <v-progress-circular
                    v-if="notesLoading"
                    indeterminate
                    color="blue"
                  />
                </center>

                <v-list two-line>
                  <template v-for="item in editedItem.activities">
                    <div :key="item.id" class="activity-item">
                      <v-divider></v-divider>
                      <v-list-item avatar>
                        <v-list-item-avatar
                          :color="getScanColor(item.Discriminator)"
                        >
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <span v-on="on">{{ item.Discriminator }}</span>
                            </template>
                            <span>{{ getScanText(item.Discriminator) }}</span>
                          </v-tooltip>
                        </v-list-item-avatar>
                        <v-list-item-content
                          @click="showNoteDetail(item.Description)"
                        >
                          <v-list-item-title>
                            {{ item.ActivityDate }}
                          </v-list-item-title>
                          <v-list-item-subtitle
                            v-html="item.Description"
                          ></v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </div>
                  </template>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <notes-internal
              :packageId="editedItem.Id"
              noteType="PrivateCourierAgent"
              :closing="isClosing"
            />
          </v-expansion-panels>
        </v-card-text>
      </v-card>
      <v-form v-model="isFormValid">
        <v-card class="edit-move-modal-v-card">
          <v-card-text>
            Delivered By:
            <v-radio-group
              class="delivered-by-rb"
              v-model="editedItem.DeliveredBy"
              mandatory
              row
            >
              <v-radio :key="0" label="Courier" :value="0"></v-radio>
              <v-radio :key="1" label="Carrier" :value="1"></v-radio>
              <v-radio :key="-1" label="None" :value="null"></v-radio>
            </v-radio-group>

            <!-- items -->
            <div
              v-for="item in billingExplanationItems"
              :key="item.key"
              style="display: flex; align-items: center; padding-bottom:10px"
            >
              <!-- checkbox -->
              <div>
                <!-- input -->
                <v-checkbox
                  :id="item.key"
                  v-model="selectedBillingExplanationItems"
                  :value="item"
                ></v-checkbox>
              </div>
              <!-- other -->
              <div>
                <!-- lines -->
                <div
                  v-for="line in item.lines"
                  :key="`${item.key}-${line.id}`"
                  style="display: flex; align-items: center;"
                >
                  <!-- labels + input -->
                  <template v-if="line.text.includes('\\d+')">
                    <!-- checkbox label -->
                    <div>
                      <label :for="item.key">
                        {{ line.text.split("\\d+")[0] }}
                      </label>
                    </div>

                    <!-- input -->
                    <v-text-field
                      :id="`${item.key}-${line.id}`"
                      v-model.number="item.value"
                      :rules="milesBillingExplanationRules"
                      style="width:70px; padding: 0px 10px 0px 10px;"
                      min="1"
                      type="number"
                    ></v-text-field>
                    <!-- input label -->
                    <label :for="`${item.key}-${line.id}`">
                      {{ line.text.split("\\d+")[1] }}
                    </label>
                  </template>
                  <!-- checkbox label -->
                  <label v-else :for="item.key">{{ line.text }}</label>
                </div>
              </div>
            </div>

            <v-text-field
              class="courier-fee-input"
              prefix="$"
              type="number"
              v-model.number="editedItem.CourierNetworkFee"
              :rules="[rulesRequired, rulesCurrency]"
              label="Courier Network Fee"
            ></v-text-field>
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12 sm6 md12>
                  <h4>Courier</h4>
                </v-flex>
                <v-flex xs12 sm6 md6>
                  <v-text-field
                    class="courier-quote-text-field"
                    prefix="$"
                    type="number"
                    v-model.number="editedItem.CourierQuote"
                    :rules="[rulesRequired, rulesCurrency]"
                    label="Quote"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md6>
                  <v-layout row>
                    <v-text-field
                      class="courier-actual-text-field"
                      prefix="$"
                      v-model.number="editedItem.CourierActual"
                      type="number"
                      label="Actual"
                      :rules="[rulesRequired, rulesCurrency]"
                    ></v-text-field>
                    <v-btn
                      v-if="editedItem.CourierActual != editedItem.ClientActual"
                      class="courier-actual-to-client-btn"
                      icon
                      @click="
                        applyAsClientActual(
                          editedItem,
                          editedItem.CourierActual
                        )
                      "
                    >
                      <v-icon>play_for_work</v-icon>
                    </v-btn>
                  </v-layout>
                </v-flex>
                <v-flex xs12 sm6 md12>
                  <h4>Client</h4>
                </v-flex>
                <v-flex xs12 sm6 md6>
                  <v-layout row>
                    <v-text-field
                      class="client-quote-text-field"
                      prefix="$"
                      v-model.number="editedItem.ClientQuote"
                      type="number"
                      label="Quote"
                    ></v-text-field>
                    <v-btn
                      v-if="editedItem.ClientQuote != editedItem.ClientActual"
                      class="justify center client-quote-to-actual-btn"
                      icon
                      @click="
                        applyAsClientActual(editedItem, editedItem.ClientQuote)
                      "
                    >
                      <v-icon>play_for_work</v-icon>
                    </v-btn>
                  </v-layout>
                </v-flex>
                <v-flex xs12 sm6 md6>
                  <v-text-field
                    class="client-actual-text-field"
                    prefix="$"
                    v-model.number="editedItem.ClientActual"
                    type="number"
                    :rules="[rulesRequired, rulesCurrency]"
                    label="Actual"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm12 md12>
                  <center>
                    <label>Actual Markup</label>
                    <h3 class="actual-markup">
                      ${{
                        (
                          editedItem.ClientActual - editedItem.CourierActual
                        ).toFixed(2)
                      }}
                    </h3>
                  </center>
                </v-flex>
              </v-layout>
            </v-container>
            <v-alert
              class="high-value-alert"
              :value="true"
              v-if="isHighValue(editedItem)"
              type="warning"
            >
              One or more of the currency fields is over $500.00. Please ensure
              this is correct and seek approval as necessary.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-btn
              class="flag-btn"
              color="#ff7c00"
              text
              @click.native="markAsNeedsReview(editedItem)"
            >
              {{ editedItem.NeedsReview ? "Unflag Move" : "Flag Move" }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              v-if="!editedItem.NeedsReview"
              class="do-not-bill-btn"
              color="red"
              text
              @click.native="saveAsDoNotBill(editedItem)"
            >
              Do Not Bill
            </v-btn>
            <v-btn
              v-if="!editedItem.NeedsReview && !editedItem.BillingApproved"
              class="save-and-approve-btn"
              color="blue darken-1"
              text
              :disabled="!isFormValid"
              @click.native="save(editedItem, true)"
            >
              Save and Approve
            </v-btn>
            <v-btn
              v-if="editedItem.BillingApproved"
              class="un-approve-btn"
              color="blue darken-1"
              text
              :disabled="!isFormValid"
              @click.native="save(editedItem, false)"
            >
              UnApprove
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
      <v-dialog v-model="noteModal" persistent max-width="290">
        <v-card>
          <v-card-text>{{ selectedNoteText }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="close-note-detail-btn"
              color="green darken-1"
              text
              @click.native="noteModal = false"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-dialog>
  </div>
  <!-- eslint-enable -->
</template>

<script>
import { BILLING_NAMESPACE } from "../store/billingStore";
import RulesMixin from "@/mixins/RulesMixin.js";
import NotesInternal from "../NotesInternal.vue";
export default {
  components: {
    "notes-internal": NotesInternal
  },
  mixins: [RulesMixin],
  data() {
    return {
      openedPanel: null,
      isClosing: false,
      selectedNoteText: null,
      editModal: false,
      noteModal: false,
      isFormValid: true,
      notesLoading: false,
      milesBillingExplanation: 1,
      milesBillingExplanationRules: [
        v => !!v || "Miles is required",
        v => v > 0 || "Miles should be greater than 0"
      ],
      selectedBillingExplanationItems: [],
      billingExplanationItems: [
        {
          key: "b1",
          lines: [
            { id: 1, text: "Courier had wait time associated with delivery." }
          ]
        },
        {
          key: "b2",
          value: 1,
          lines: [
            {
              id: 1,
              text: "Courier had \\d+ additional attempt(s) at delivery. "
            },
            { id: 2, text: "Courier hold and attempt charges applied." }
          ]
        },
        {
          key: "b3",
          lines: [
            {
              id: 1,
              text:
                "Consignee wasn't available for immediate delivery. Courier had to hold & refrigerate."
            }
          ]
        },
        {
          key: "b4",
          lines: [
            {
              id: 1,
              text:
                "Courier attempted delivery; this was then held until returned to sender per Client request."
            }
          ]
        },
        {
          key: "b5",
          lines: [
            {
              id: 1,
              text: "This was held until returned to sender per Client request."
            }
          ]
        },
        {
          key: "b6",
          lines: [
            {
              id: 1,
              text:
                "Package not available for pickup until late from carrier. Courier had to hold and refrigerate and deliver next business day."
            }
          ]
        },
        {
          key: "b7",
          value: 1,
          lines: [
            {
              id: 1,
              text:
                "Carrier did not have the package available at the committed time. "
            },
            {
              id: 2,
              text: "Courier had \\d+ additional attempt(s) at recovery."
            }
          ]
        },
        {
          key: "b8",
          lines: [
            {
              id: 1,
              text:
                "Carrier did not have the package available at the committed time - Courier had wait time at carrier facility."
            }
          ]
        },
        {
          key: "b9",
          lines: [
            {
              id: 1,
              text: "This was quoted for a hold but was delivered same day."
            }
          ]
        },
        {
          key: "b10",
          lines: [{ id: 1, text: "Courier attempt recovery charges apply." }]
        },
        {
          key: "b11",
          value: 1,
          lines: [
            {
              id: 1,
              text: "Approved redirect. Additional mileage of \\d+ miles."
            }
          ]
        },
        {
          key: "b12",
          lines: [
            {
              id: 1,
              text:
                "Additional mileage for the courier that the system did not allocate for."
            }
          ]
        },
        { key: "b13", lines: [{ id: 1, text: "Weekend/AfterHours Fee" }] },
        { key: "b14", lines: [{ id: 1, text: "Fuel surchage/Toll Fee" }] },
        {
          key: "b15",
          lines: [{ id: 1, text: "Delivery fee was not included in the quote" }]
        },
        { key: "b16", lines: [{ id: 1, text: "Multipiece shipment" }] },
        {
          key: "b17",
          lines: [
            {
              id: 1,
              text:
                "No explanation needed- billed as quoted (quote value or quote+$10)"
            }
          ]
        }
      ],
      editedItem: {
        TrackingNumber: ""
      }
    };
  },
  created: /* istanbul ignore next */ function() {
    this.$parent.$on("edit-courier-move", this.show);
  },
  methods: {
    markAsNeedsReview(item) {
      var data = _.cloneDeep(item);
      return this.$store
        .dispatch(`${BILLING_NAMESPACE}/FlagItem`, data)
        .then(x => {
          this.editedItem = x;
          this.$forceUpdate();
        });
    },
    applyAsClientActual(item, value) {
      item.ClientActual = value;
    },
    isHighValue(item) {
      if (
        item.CourierQuote > 500.0 ||
        item.CourierActual > 500.0 ||
        item.ClientQuote > 500.0 ||
        item.ClientActual > 500.0
      )
        return true;
      return false;
    },
    showNoteDetail(content) {
      this.selectedNoteText = content;
      this.noteModal = true;
    },
    loadNotes() {
      if (this.editedItem.notes == null) {
        this.notesLoading = true;
        this.$forceUpdate();
        return this.$store
          .dispatch(`${BILLING_NAMESPACE}/LOAD_NOTES`, this.editedItem.Id)
          .then(x => {
            this.editedItem.notes = x.notes;
            this.editedItem.activities = x.activities;
            this.notesLoading = false;
            this.$forceUpdate();
          });
      }
    },
    saveAsDoNotBill(item) {
      item.DoNotBill = true;
      return this.$store
        .dispatch(`${BILLING_NAMESPACE}/SAVE_DO_NOT_BILL`, item)
        .then(x => {
          this.editModal = false;
          item.DoNotBill = true;
          item.Approved = false;
          this.editedItem = {};
        });
    },
    close() {
      this.isClosing = !this.isClosing;
      this.openedPanel = null;
      this.editModal = false;
      this.editedItem = {};
    },
    getUserAbbreviation(note) {
      if (!note) return "";
      return note.match(/\b(\w)/g)[0].toUpperCase();
    },
    getUserColor(note) {
      if (note.Content == null) return "white";
      if (note.Content.includes("CLIENT:")) return "purple lighten-4";
      if (note.CreatedBy != null) return "blue lighten-1";
      else return "blue lighten-4";
    },
    getUserTip(note) {
      if (note.Content == null) return "";
      if (note.Content.includes("CLIENT:")) return "Client note";
      if (note.CreatedBy != null) return "ParcelShield agent note";
      else return "System note";
    },
    getScanColor(discriminator) {
      switch (discriminator) {
        case "CD":
        case "CP":
          return "green";
        case "C":
          return "blue lighten-1";
        case "EX":
          return "red lighten-1";
        default:
          return "brown lighten-3";
      }
    },
    getScanText(discriminator) {
      switch (discriminator) {
        case "CD":
        case "CP":
          return "Courier Scan";
        case "C":
          return "ParcelShield Scan";
        case "EX":
          return "Carrier Exception";
        default:
          return "Carrier Scan";
      }
    },
    joinBillingExplanation(explanations) {
      var selectedExplanations = [];
      explanations.forEach(element => {
        var translatedExplanation = "";
        element.lines.forEach(line => {
          if (line.text.indexOf("\\d+")) {
            if (element.value == "") element.value = 0;

            translatedExplanation += line.text.replace("\\d+", element.value);
          } else {
            translatedExplanation += line.text;
          }
        });
        selectedExplanations.push(translatedExplanation);
      });

      return selectedExplanations.join(";");
    },
    splitBillingExplanation(explanation) {
      if (explanation == null) return [];
      var explanations = explanation.split(";");
      var translatedExplanations = [];

      this.billingExplanationItems.forEach(b => {
        b.value = 1;
        var returnValue = explanations.forEach((e, ei, a) => {
          var explanation = "";
          b.lines.forEach(l => {
            explanation += l.text;
          });

          if (explanation.indexOf("\\d+") >= 0) {
            var matchArray = e.match(/\d+/);
            if (matchArray && explanation.replace("\\d+", matchArray[0]) == e) {
              b.value = matchArray[0];
              explanation = explanation.replace("\\d+", matchArray[0]);
            }
          }

          if (e === explanation) {
            translatedExplanations.push(b);
            a.splice(ei, 1);
            return false;
          }
          return true;
        });
      });

      this.selectedBillingExplanationItems = translatedExplanations;
    },
    save(item, approved) {
      item.BillingExplanation = this.joinBillingExplanation(
        this.selectedBillingExplanationItems
      );

      item.BillingApproved = approved;
      return this.$store
        .dispatch(`${BILLING_NAMESPACE}/SAVE_APPROVED_MOVE`, item)
        .then(x => {
          this.editModal = false;
          item.DoNotBill = false;
          item.BillingApproved = approved;
          this.editedItem = {};
        });
    },
    getDeliveredByText(enumCode) {
      if (enumCode === 0) return "Courier";
      if (enumCode === 1) return "Carrier";
      return "";
    },
    show(item) {
      this.splitBillingExplanation(item.BillingExplanation);
      this.editedItem = item;
      this.editModal = true;
    }
  }
};
</script>
